import { useState, useMemo, useEffect, useContext } from "react";
import {
    Box, Typography, Grid, MenuItem, Select,
    FormControl, InputLabel, CircularProgress, Alert, Paper, Divider
} from "@mui/material";
import { DateRangePicker, TagPicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import dayjs from "dayjs";
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { EntityContext } from "../../../../contexts/EntityContext";
import reportsApi from "../../../../shared/utils/reportsApi";
import "./styles.css";

const PROPERTIES = [
    { id: "11", title: "Balboa Bay Resort" },
    { id: "2", title: "Anaheim Portofino Inn & Suites" },
    { id: "181", title: "Dollywood HeartSong Lodge and Resort" },
    { id: "14", title: "Boston Harbor Hotel" },
    { id: "7", title: "Autocamp Catskills" },
    { id: "103", title: "The Hermitage Hotel" },
    { id: "9", title: "Autocamp Russian River" },
    { id: "23", title: "Eau Palm Beach Resort & Spa" },
    { id: "26", title: "Enchantment Resort" },
    { id: "60", title: "Mii amo" },
    { id: "21", title: "Dollywood DreamMore Resort" },
    { id: "8", title: "Autocamp Joshua Tree" },
    { id: "27", title: "Evo Hotel Tahoe City" },
    { id: "3", title: "Arnold Palmer's Bay Hill Club & Lodge" },
    { id: "73", title: "Parmer's Resort" }
];

const LINE_COLORS = ["#1976d2", "#2e7d32", "#ed6c02", "#9c27b0", "#d32f2f", "#0288d1"];

const Rollup = () => {
    const { setEntityName } = useContext(EntityContext);
    const [groupId, setGroupId] = useState<string>("16");
    const [propertyId, setPropertyId] = useState<string>("");
    const [dataSources, setDataSources] = useState<string[]>([]);
    const [dateRange, setDateRange] = useState<[Date, Date] | null>([
        dayjs().subtract(7, 'day').toDate(),
        dayjs().toDate()
    ]);

    const [response, setResponse] = useState<{ loading: boolean, data: any, error: any }>({
        loading: false,
        data: null,
        error: null
    });

    useEffect(() => {
        setEntityName({ entityName: "Rollup", entityTitle: "Multi Property Rollup" });
    }, [setEntityName]);

    const fetchRollupData = async () => {
        if (!dateRange || (!propertyId && !groupId)) return;
        setResponse(prev => ({ ...prev, loading: true, error: null }));

        const startDate = dayjs(dateRange[0]).format("YYYY-MM-DD");
        const endDate = dayjs(dateRange[1]).format("YYYY-MM-DD");

        try {
            const res = await reportsApi.get(`/reports/multi-property-rollups`, {
                params: {
                    startDate,
                    endDate,
                    property_id: propertyId || undefined,
                    group_id: groupId || undefined,
                    data_source: dataSources.length > 0 ? dataSources : undefined,
                    offset: 0,
                    limit: 100
                },
                headers: { "Cache-Control": "no-cache" }
            });

            if (!res.data || (Object.keys(res.data).length === 0 && localStorage.getItem("gcomToken"))) {
                fetchRollupData();
                return;
            }

            setResponse({ loading: false, data: res.data, error: null });
        } catch (err: any) {
            setResponse({ loading: false, data: null, error: err.message });
        }
    };

    useEffect(() => {
        fetchRollupData();
    }, [propertyId, groupId, dateRange, dataSources]);

    const { chartData, propertyKeys } = useMemo(() => {
        if (!response.data?.items) return { chartData: [], propertyKeys: [] };

        const propertySet = new Set<string>();
        const aggregated = response.data.items.reduce((acc: any, item: any) => {
            const date = item.event_date;
            const propName = item.property?.title || "Unknown Property";

            const hasData = (parseInt(item.total_clicks) || 0) > 0 || (parseFloat(item.total_cost) || 0) > 0;
            if (!hasData && propertyId !== "") return acc;

            propertySet.add(propName);
            if (!acc[date]) acc[date] = { date };

            acc[date][`${propName}_clicks`] = (acc[date][`${propName}_clicks`] || 0) + parseInt(item.total_clicks || "0");
            acc[date][`${propName}_impressions`] = (acc[date][`${propName}_impressions`] || 0) + parseInt(item.total_impressions || "0");
            acc[date][`${propName}_cost`] = (acc[date][`${propName}_cost`] || 0) + parseFloat(item.total_cost || "0");
            acc[date][`${propName}_bookings`] = (acc[date][`${propName}_bookings`] || 0) + (item.total_bookings || 0);
            acc[date][`${propName}_revenue`] = (acc[date][`${propName}_revenue`] || 0) + parseFloat(item.total_revenue || "0");
            acc[date][`${propName}_roas`] = parseFloat(item.roas || "0");

            return acc;
        }, {});

        const sortedData = Object.values(aggregated).sort((a: any, b: any) => dayjs(a.date).unix() - dayjs(b.date).unix());
        return { chartData: sortedData, propertyKeys: Array.from(propertySet) };
    }, [response.data, propertyId]);

    const renderChart = (title: string, dataKeyType: string, isCurrency = false) => (
        <Grid item xs={12} md={6} lg={6}>
            <Paper elevation={1} sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: '12px', bgcolor: '#fff' }}>
                <Box className="graphHeader" sx={{ mb: 2, mx: -2, mt: -2, borderTopLeftRadius: '12px', borderTopRightRadius: '12px', bgcolor: '#f7f7f7' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333' }}>{title}</Typography>
                </Box>
                <Box sx={{ height: 300, width: "100%", mt: 1 }}>
                    <ResponsiveContainer>
                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                            <XAxis dataKey="date" tickFormatter={(t) => dayjs(t).format("MM/DD")} style={{ fontSize: "11px", fill: '#888' }} />
                            <YAxis style={{ fontSize: "11px", fill: '#888' }} tickFormatter={(v) => isCurrency ? `$${v.toFixed(0)}` : v} />
                            <Tooltip />
                            <Legend verticalAlign="top" height={36} />
                            {propertyKeys.map((prop, index) => (
                                <Area
                                    key={prop}
                                    type="monotone"
                                    name={prop}
                                    dataKey={`${prop}_${dataKeyType}`}
                                    stroke={LINE_COLORS[index % LINE_COLORS.length]}
                                    fill={LINE_COLORS[index % LINE_COLORS.length]}
                                    fillOpacity={0.05}
                                    strokeWidth={2}
                                    dot={{ r: 2 }}
                                />
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            </Paper>
        </Grid>
    );

    return (
        <Box className="dashBoardMain" sx={{ pt: { xs: 2, sm: 3 }, pl: { xs: 2, sm: 6 }, pr: { xs: 2, sm: 5 }, pb: 2, width: "100%" }}>
            <Box sx={{ p: { xs: 2, sm: 3 }, width: "100%", backgroundColor: { xs: "transparent", md: "#9E9E9E1A" }, borderRadius: "12px" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
                    Multi Property Performance Rollup
                </Typography>

                <Grid container spacing={2} sx={{ mb: 4 }}>

                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small" sx={{ bgcolor: '#fff', borderRadius: '4px' }}>
                            <InputLabel>Group</InputLabel>
                            <Select value={groupId} label="Group" onChange={(e) => setGroupId(e.target.value as string)}>
                                <MenuItem value=""><em>None</em></MenuItem>
                                <MenuItem value="16">Group 16</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small" sx={{ bgcolor: '#fff', borderRadius: '4px' }}>
                            <InputLabel>Property</InputLabel>
                            <Select value={propertyId} label="Property" onChange={(e) => setPropertyId(e.target.value as string)}>
                                <MenuItem value=""><em>None (Show All in Group)</em></MenuItem>
                                {PROPERTIES.map((p) => <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <DateRangePicker value={dateRange} onChange={(val) => setDateRange(val)} format="yyyy-MM-dd" style={{ width: '100%' }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TagPicker data={["SA360", "FacebookAds", "GoogleAds", "Derbysoft", "DV360", "CM360"].map(v => ({ label: v, value: v }))} style={{ width: '100%' }} placeholder="Data Sources" value={dataSources} onChange={setDataSources} />
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 4, opacity: 0.6 }} />

                {response.loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>
                ) : response.error ? (
                    <Alert severity="error">{response.error}</Alert>
                ) : chartData.length === 0 ? (
                    <Box sx={{ py: 10, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">No performance data found.</Typography>
                        <Typography variant="body2" color="text.secondary">Try adjusting your filters or date range.</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {renderChart("Clicks", "clicks")}
                        {renderChart("Impressions", "impressions")}
                        {renderChart("Cost", "cost", true)}
                        {renderChart("Bookings", "bookings")}
                        {renderChart("Revenue", "revenue", true)}
                        {renderChart("ROAS", "roas")}
                    </Grid>
                )}
            </Box>
        </Box>
    );
};

export default Rollup;